import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Textarea } from "@air/primitive-textarea";
import { tailwindMerge } from "@air/tailwind-variants";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { InlineError } from "./InlineError";
import { removeNewlines } from "./utils";
export const InlineInput = /*#__PURE__*/ memo((param)=>{
    let { error, onCancel, onSubmit, onChange, defaultValue = "", validate, className, inputClassName, label, allowNewLines = true, ...restProps } = param;
    const [value, setValue] = useState(defaultValue);
    const [internalError, setInternalError] = useState();
    const textAreaRef = useRef(null);
    const errorToShow = error || internalError;
    const hasError = !!errorToShow;
    useEffect(()=>{
        setValue(defaultValue);
    }, [
        defaultValue
    ]);
    const submitValue = useCallback((value)=>{
        if (!hasError) {
            onSubmit === null || onSubmit === void 0 ? void 0 : onSubmit(value === null || value === void 0 ? void 0 : value.trim());
        }
    }, [
        hasError,
        onSubmit
    ]);
    const handleEscOnKeyUp = useCallback((event)=>{
        if (event.key === "Escape") {
            event.stopPropagation();
            onCancel === null || onCancel === void 0 ? void 0 : onCancel();
        }
    }, [
        onCancel
    ]);
    const handleBlur = useCallback(()=>{
        submitValue(value);
    }, [
        submitValue,
        value
    ]);
    const onValueChange = useCallback((fieldValue)=>{
        setValue(fieldValue);
        onChange === null || onChange === void 0 ? void 0 : onChange(fieldValue);
        if (!!validate) {
            setInternalError(validate(fieldValue));
        } else {
            setInternalError(undefined);
        }
    }, [
        onChange,
        validate
    ]);
    const onInputChange = useCallback((event)=>{
        event.target.value = allowNewLines ? event.target.value : removeNewlines(event.target.value);
        onValueChange(event.target.value);
    }, [
        onValueChange,
        allowNewLines
    ]);
    const handleKeyDown = useCallback((event)=>{
        if (event.key === "Enter") {
            event.stopPropagation();
            if (event.shiftKey) {
                if (!allowNewLines) {
                    event.preventDefault();
                }
            } else {
                var _textAreaRef_current;
                event.preventDefault();
                (_textAreaRef_current = textAreaRef.current) === null || _textAreaRef_current === void 0 ? void 0 : _textAreaRef_current.blur();
            }
        }
        if (event.key === "Enter" && !event.shiftKey) {
            var _textAreaRef_current1;
            event.stopPropagation();
            event.preventDefault();
            (_textAreaRef_current1 = textAreaRef.current) === null || _textAreaRef_current1 === void 0 ? void 0 : _textAreaRef_current1.blur();
        }
    }, [
        allowNewLines
    ]);
    const handleFocus = useCallback((event)=>{
        const textareaValue = event.target.value;
        event.target.setSelectionRange(textareaValue.length, textareaValue.length);
    }, []);
    return /*#__PURE__*/ _jsxs("div", {
        className: tailwindMerge("relative flex flex-1 flex-col", className),
        children: [
            /*#__PURE__*/ _jsx(Textarea, {
                ref: textAreaRef,
                "aria-label": label,
                autoFocus: true,
                className: tailwindMerge("resize-none overflow-x-hidden px-2 py-0", allowNewLines ? "whitespace-pre-wrap" : "whitespace-nowrap", inputClassName),
                onBlur: handleBlur,
                onFocus: handleFocus,
                hasError: !!errorToShow,
                onKeyDown: handleKeyDown,
                onKeyUp: handleEscOnKeyUp,
                value: value,
                onChange: onInputChange,
                ...restProps
            }),
            /*#__PURE__*/ _jsx(InlineError, {
                "data-testid": restProps["data-testid"] ? "".concat(restProps["data-testid"], "-error") : "INLINE_INPUT_ERROR",
                error: errorToShow
            })
        ]
    });
});
InlineInput.displayName = "InlineInput";
